<div [ngClass]="{ invisible: isLoading, 'h-0': isLoading }" class="table-wrapper">
    <div fxLayout="row" fxLayoutAlign="space-between center" class="white px-24 py-12">
        <h2 class="text-base">{{ 'PAGE.USER_FORM.TAB.CERTIFICATES.TITLE' | translate }}</h2>
        <div fxLayout="row" fxLayoutAlign="end center">
            <app-search-outline [searchInput]="searchInput"></app-search-outline>
            <app-flat-button
                class="ml-16"
                [icon]="'add_circle'"
                [label]="'PAGE.CERTIFICATE.BUTTON.ADD'"
                (onClick)="openAddCertificateDialog()"
                *ngIf="canShowAddCertificate"
            ></app-flat-button>
        </div>
    </div>
    <div [class.hidden]="!(userCourseCertificates$ | async)?.length">
        <mat-table #table [dataSource]="dataSource" matSort [matSortActive]="sortActive" [matSortDirection]="sortDirection" matSortDisableClear>
            <!-- Provider Column -->
            <ng-container matColumnDef="provider">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'PAGE.CERTIFICATIONS.LABEL.PROVIDER' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <ng-container *ngIf="certificate.officialCertificateMetadata?.certification?.provider?.providerName as providerName; else noProvider">
                        <span class="text-truncate" [tooltipIfTruncated]="providerName">{{ providerName }}</span>
                    </ng-container>
                    <ng-template #noProvider>
                        <span>-</span>
                    </ng-template>
                </mat-cell>
            </ng-container>

            <!-- Qualification Name Column -->
            <ng-container matColumnDef="name">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'PAGE.CERTIFICATIONS.LABEL.CERTIFICATION' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <span
                        class="text-truncate"
                        [class.pointer]="!isMyTeamsPage"
                        [tooltipIfTruncated]="certificate.officialCertificateMetadata?.certification?.certificationName || certificate.name"
                        (click)="!isMyTeamsPage ? openCertificate(certificate) : null"
                        >{{ certificate.officialCertificateMetadata?.certification?.certificationName || certificate.name }}</span
                    >
                </mat-cell>
            </ng-container>

            <!-- Course Column -->
            <ng-container matColumnDef="course">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'MODULE.SINGULAR' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <ng-container *ngIf="(certificate.finalGrade?.module || certificate.moduleUser?.module) as module; else noCourse">
                        <div
                            fxLayout="row"
                            fxLayoutAlign="start center"
                            class="bg-gray-100 py-8 pr-16 pl-8 rounded-full max-w-60 text-truncate"
                            [ngClass]="{ 'pointer hover:bg-gray-200': canNavigateToModule }"
                            (click)="canNavigateToModule ? goToModule(module) : null"
                        >
                            <div class="shrink-0" *ngIf="module.image; else noModuleImage">
                                <img class="avatar !h-8 !w-8 !min-w-8" [alt]="module.name" [src]="module.image.thumbUrl" />
                            </div>
                            <ng-template #noModuleImage>
                                <div class="avatar module-icon-container no-image !h-8 !w-8 !min-w-8 shrink-0">
                                    <mat-icon class="bg-icon" svgIcon="bg_illustration"></mat-icon>
                                </div>
                            </ng-template>
                            <mat-icon class="icon-18 mr-8 shrink-0" [svgIcon]="module | getModuleIcon"></mat-icon>
                            <span class="text-truncate mr-8 min-w-0 font-medium" [tooltipIfTruncated]="module.name">{{ module.name }}</span>
                            <mat-icon class="icon-18 shrink-0" *ngIf="canNavigateToModule">keyboard_arrow_right</mat-icon>
                        </div>
                    </ng-container>
                    <ng-template #noCourse>
                        <span>-</span>
                    </ng-template>
                </mat-cell>
            </ng-container>

            <!-- Issue date Column -->
            <ng-container matColumnDef="issueDate">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'PAGE.CERTIFICATIONS.LABEL.ISSUE_DATE' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <span>{{ (certificate.officialCertificateMetadata ? certificate.officialCertificateMetadata?.issueDate : certificate.createdAt) | date: 'shortDate' }}</span>
                </mat-cell>
            </ng-container>

            <!-- Expiration date Column -->
            <ng-container matColumnDef="expirationDate">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'PAGE.CERTIFICATIONS.LABEL.EXPIRATION_DATE' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <ng-container *ngIf="certificate.officialCertificateMetadata?.expirationDate; else noExpirationDate">
                        <span [style.color]="certificate.officialCertificateMetadata.expirationDate | certificationExpirationColor">{{
                            certificate.officialCertificateMetadata.expirationDate | date: 'shortDate'
                        }}</span>
                        <mat-icon class="ml-4 icon-20" [svgIcon]="certificate.officialCertificateMetadata.expirationDate | certificationExpirationIcon"></mat-icon>
                    </ng-container>
                    <ng-template #noExpirationDate>
                        <span>-</span>
                    </ng-template>
                </mat-cell>
            </ng-container>

            <!-- Status Column -->
            <ng-container matColumnDef="status">
                <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'GENERAL.PROPERTY.STATUS' | translate }}</mat-header-cell>
                <mat-cell *matCellDef="let certificate">
                    <ng-container *ngIf="getStatusLabel(certificate) as statusLabel; else noStatus">
                        <span class="text-truncate" [tooltipIfTruncated]="statusLabel | translate">{{ statusLabel | translate }}</span>
                    </ng-container>
                    <ng-template #noStatus>
                        <span>-</span>
                    </ng-template>
                </mat-cell>
            </ng-container>

            <!-- Buttons Column -->
            <ng-container matColumnDef="buttons">
                <mat-header-cell *matHeaderCellDef></mat-header-cell>
                <mat-cell *matCellDef="let certificate; let i = index">
                    <div fxFlex="row" fxLayoutAlign="end center">
                        <button mat-icon-button [matMenuTriggerFor]="moreMenu" aria-label="More" (click)="$event.stopPropagation()" *ngIf="hasRowActions(certificate)">
                            <mat-icon class="mat-icon-more-vert">more_vert</mat-icon>
                        </button>
                        <mat-menu #moreMenu="matMenu" xPosition="before">
                            <button mat-menu-item aria-label="edit" (click)="openCertificate(certificate)" *ngIf="canUserManageCertificates && !isModuleCertificate(certificate)">
                                <mat-icon class="mr-8">edit</mat-icon>
                                <span>{{ 'GENERAL.BUTTON.EDIT' | translate }}</span>
                            </button>
                            <button
                                mat-menu-item
                                aria-label="replace"
                                (click)="openAddReplaceCertificateDefaultDialog(certificate)"
                                *ngIf="canUserManageCertificates && canReplaceCertificate(certificate)"
                            >
                                <mat-icon class="mr-8" svgIcon="replace"></mat-icon>
                                <span>{{ 'GENERAL.BUTTON.REPLACE' | translate }}</span>
                            </button>
                            <button mat-menu-item aria-label="download" (click)="downloadCertificate(certificate)" *ngIf="certificate.filename && canDownloadCertificates">
                                <mat-icon class="mr-8">get_app</mat-icon>
                                <span>{{ 'GENERAL.BUTTON.DOWNLOAD' | translate }}</span>
                            </button>
                            <button mat-menu-item aria-label="remove" (click)="removeCertificate(certificate)" *ngIf="canUserManageCertificates">
                                <mat-icon class="mr-8">delete</mat-icon>
                                <span>{{ 'GENERAL.BUTTON.REMOVE' | translate }}</span>
                            </button>
                        </mat-menu>
                    </div>
                </mat-cell>
            </ng-container>

            <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
            <mat-row *matRowDef="let certificate; columns: displayedColumns" class="certificate" matRipple> </mat-row>
        </mat-table>

        <mat-paginator persistPageSize="certificate-record.user-course-certificates-list" [length]="userCourseCertificatesTotalCount$ | async" [pageSize]="10" [pageSizeOptions]="[5, 10, 25, 50]"></mat-paginator>
    </div>
</div>

<div class="white" fxLayout="column" fxLayoutAlign="center center" *ngIf="isLoading">
    <div class="shimmer-container min-w-full p-0">
        <div class="shimmer p-12">
            <div class="shimmer-card !max-w-full table h-480 animate"></div>
        </div>
    </div>
</div>

<ng-container *ngIf="!isLoading && !(userCourseCertificates$ | async)?.length">
    <div class="no-options-wrapper white mt-16" fxLayoutAlign="center center">
        <div class="no-options-container" fxLayout="column">
            <div fxLayout="column" fxLayoutAlign="center center">
                <mat-icon svgIcon="certificate_illustration" class="participant-view-illustration"></mat-icon>
                <p class="mt-12 mb-24">{{ 'PAGE.CERTIFICATION_PROVIDER.LABEL.NO_CERTIFICATES' | translate }}</p>
            </div>
        </div>
    </div>
</ng-container>
